import WithLoadingIndicator from 'components/page-loader/page-loader';
import type React from 'react';
import { useEffect, useState } from 'react';

import './gridStyles.less';
import { ThemeProvider } from '@mui/material/styles';
import { Col, Row } from 'antd';
import { NavigateToCinder } from 'components/navigate-to-cinder';
import { PreviewGrid } from 'components/preview';
import { DOWNLOADED_FONTS } from 'components/editor/grid/reduxStore/defaultTextDefaultValue';
import Banner from 'components/preview/banner';
import { IdentifyToViewModal } from 'components/preview/identify-viewer/IdentifyToViewModal';
import { PreviewMenu } from 'components/preview/PreviewMenu';
import { useTrackViewing } from 'components/preview/track-viewer/useTrackViewing';
import SEO from 'components/seo';
import { useTranslation } from 'react-i18next';
import { useIsAuthenticated } from 'hooks/useIsAuthenticated';
import { CINDER_URLS } from 'utils/cinder-urls';
import FontLoader from 'components/font';
import DefaultFontLoader from 'components/font/DefaultFontLoader';
import { PreviewActionMenu } from 'components/preview/ActiveMenu';
import { PreviewTableOfContents } from 'components/preview/PreviewTableOfContents';
import ProposifyTheme from 'muiTheme/PyTheme';
import { usePreviewVariables } from 'providers/PreviewVariablesContext';
import type { GetDocumentResponse, SectionByDocumentIdApiResponse } from 'services/documents/DocumentRepository';
import { PricingTotalType } from 'services/documents/entities';
import PreviewCanvas from '../preview/PreviewCanvas';
import PdfDownloadButtonForExportingService from 'components/preview/Download/PdfDownloadButtonForExportingService';
import { LiveblocksProvider } from 'providers/LiveblocksProvider';
import { ContentType } from 'utils/getContentType';
import useDocumentStyleForPreview from 'pages/preview/documentStyleForPreviewProps';
import { getOrientationFromPageSize, resolvePageSize } from 'components/editor/shared/gridConfig';
import type { UserEntity } from 'services/entities/UserEntity';
import { useSectionsForPreview } from 'pages/editor/useSectionsForPreview';
import { useDocumentExpiryDate } from 'hooks/useDocumentExpiryDate';
import { PreviewInsights } from 'components/Insights/Insights';
import { formatCurrencyValue } from 'components/editor/helpers/formatCurrencyValue.ts';

const selectDocumentTotal = (sections: SectionByDocumentIdApiResponse[]): number => {
  let documentTotal = 0;

  for (const sectionId in sections) {
    let sectionTotal = 0;
    Object.values(sections[sectionId]?.blocks).forEach((blockContent) => {
      const tableTotal = blockContent.content?.metadata.tableTotal;
      if (tableTotal !== undefined) {
        sectionTotal += tableTotal;
      }
    });
    documentTotal += sectionTotal;
  }
  return documentTotal;
};

interface PreviewGridPageProps {
  documentData: GetDocumentResponse | undefined;
  userData: UserEntity | undefined;
}

export const PreviewGridPage: React.FC<PreviewGridPageProps> = ({ userData, documentData }) => {
  const { t } = useTranslation();
  const {
    sections: sectionData,
    sectionsQueryStatus: gridQueryStatus,
    documentFrequencyTotals,
    documentPresentFrequencies,
  } = useSectionsForPreview();
  const documentId = documentData?.documentId ?? '';
  const [, setPreviewConfig] = useState<any>(undefined);
  const { documentStyle, documentStyleQueryStatus } = useDocumentStyleForPreview(documentData?.contentId || '');
  const { variables, updateVariable } = usePreviewVariables();
  const isAuthenticated = useIsAuthenticated() === 'authenticated';
  const viewerData: Record<string, string> = {
    id: String(userData?.id) || '',
    email: userData?.email || '',
  };

  const totalValue = (variables?.find((variable) => variable.name === 'total')?.value as number) || 0;
  const documentTitle = documentData?.title || 'Untitled Document';
  const documentProspectName = documentData?.prospect?.name || '';
  const sections = sectionData ? sectionData.sections : [];
  const contentId = documentData?.contentId || '';
  const [isFontLoaded, setIsFontLoaded] = useState<boolean>(false);

  const { getDocumentExpiryDate } = useDocumentExpiryDate({ contentId });
  const { isLoading: isDocumentExpiryDateLoading } = getDocumentExpiryDate;

  const pageSize = resolvePageSize(documentStyle?.canvas?.['pageSize']);
  const canvasOrientation = getOrientationFromPageSize(pageSize);
  const { width: canvasWidth, height: canvasMinHeight } = pageSize;
  const currencySettings = documentStyle?.canvas?.['currencySettings'];
  const formattedDocumentPrice =
    documentData?.price !== undefined ? formatCurrencyValue(documentData.price, 'en-US', currencySettings?.currencySign) : undefined;

  const status = documentData ? 'success' : 'pending';

  useTrackViewing({ documentId, accountId: documentData?.accountId });

  useEffect(() => {
    if (!sectionData || documentData?.totalType === PricingTotalType.MANUAL) return;
    const updatedTotalValue = selectDocumentTotal(sectionData.sections);
    updateVariable('total', +updatedTotalValue);
  }, [sectionData, documentData]);

  const defaultTextStylingFonts = DOWNLOADED_FONTS.map((font) => ({
    path: font.path,
  }));

  const editorConfigHandler = (newConfig) => {
    setPreviewConfig(newConfig);
  };

  const isLoading =
    status === 'pending' ||
    isDocumentExpiryDateLoading ||
    !isFontLoaded ||
    gridQueryStatus === 'pending' ||
    documentStyleQueryStatus === 'pending' ||
    !sectionData?.sections;

  if (isLoading) {
    return (
      <>
        <WithLoadingIndicator isLoading={true} isLoaded={false} />
        {!isFontLoaded && (
          <FontLoader contentId={contentId} editorConfigHandler={editorConfigHandler} fontsFetchedCb={() => setIsFontLoaded(true)} />
        )}
      </>
    );
  }

  if (gridQueryStatus === 'error' || documentStyleQueryStatus === 'error' || !documentData) {
    return <NavigateToCinder path={CINDER_URLS.error} />;
  }

  return (
    <LiveblocksProvider contentId={contentId} contentType={ContentType.DOCUMENT} isPrivate={false}>
      <ThemeProvider theme={ProposifyTheme}>
        <PdfDownloadButtonForExportingService pageSize={pageSize} />
        <SEO title={`${t('document.preview.seo.title', { document_name: documentTitle })}`} />
        <IdentifyToViewModal
          senderName={documentData?.assignedUser?.name || ''}
          documentId={documentId}
          documentStatus={documentData.status}
          contentId={contentId}
          accountId={documentData.accountId}
          createdBy={documentData.createdBy}
          assigneeUserId={documentData.assignedUser?.id}
          documentTitle={documentTitle}
          userData={viewerData}
        />
        <DefaultFontLoader defaultFonts={defaultTextStylingFonts} />
        <FontLoader contentId={contentId} editorConfigHandler={editorConfigHandler} />
        <PreviewGrid
          canvasWidth={canvasWidth}
          canvasMinHeight={canvasMinHeight}
          orientation={canvasOrientation}
          contentId={contentId}
          header={
            <PreviewMenu
              documentId={documentId}
              contentId={contentId}
              documentTitle={documentTitle}
              documentProspectName={documentProspectName}
              documentPrice={totalValue}
              isDocumentBelongsToSameAccount={documentData.isDocumentBelongsToSameAccount}
              displayDocumentTotal={documentData.displayDocumentTotal ?? true}
              currencySettings={currencySettings}
              frequencyTotals={documentFrequencyTotals}
              presentFrequencies={documentPresentFrequencies}
            />
          }
          banner={<Banner contentId={contentId} />}
          leftAside={<PreviewTableOfContents sections={sections} />}
          main={<PreviewCanvas sections={sections} documentData={documentData} documentStyle={documentStyle} />}
          footer={
            <Row justify="space-between" align="middle">
              {(documentData.displayDocumentTotal ?? true) && (
                <Col>
                  <div className="preview__footer__document_price">{formattedDocumentPrice}</div>
                </Col>
              )}
              {isAuthenticated ? (
                <Col>
                  <PreviewActionMenu
                    contentId={contentId}
                    documentId={documentId}
                    documentTitle={documentTitle}
                    documentProspectName={documentProspectName}
                  />
                </Col>
              ) : null}
            </Row>
          }
        />
        <PreviewInsights user={userData} documentData={documentData} />
      </ThemeProvider>
    </LiveblocksProvider>
  );
};
